import React, { useEffect, useMemo } from 'react'
import { type ColumnDef } from '@tanstack/react-table'
import { useNavigate } from 'react-router-dom'
import DataTable from '../../components/DataTable'
import Button from '../components/Button'
import Icon from '../components/Icon'
import { useClassStore } from '../../stores/classesStore'
import { useSchoolStore } from '../../stores/schoolsStore'
import { useUsersStore } from '../../stores/usersStore'
import { Class, School, User } from '../../types'

const UserClasses: React.FC = () => {
  const navigate = useNavigate()

  const {
    teacherClasses,
    teacherClassesLoading,
    teacherClassesError,
    fetchTeacherClasses,
  } = useClassStore()

  const { schools, loading: schoolsLoading, fetchSchools } = useSchoolStore()

  const { users, loading: usersLoading, fetchUsers } = useUsersStore()

  // Fetch teacherClasses, schools, and users on component mount
  useEffect(() => {
    fetchTeacherClasses()
    fetchSchools()
    fetchUsers()
  }, [fetchTeacherClasses, fetchSchools, fetchUsers])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getSchoolName = (schoolId: string) => {
    const school = schools.find((school: School) => school._id === schoolId)
    return school?.displayName || 'Unknown School'
  }

  const formatTeachers = (
    classTeachers:
      | string[]
      | Array<{ _id: string; firstName: string; lastName: string }>,
  ) => {
    if (classTeachers.length === 0) return 'No teachers assigned'

    // Check if teachers are populated (objects) or just IDs (strings)
    if (typeof classTeachers[0] === 'object') {
      // Teachers are populated, extract names directly
      return (
        classTeachers as Array<{
          _id: string
          firstName: string
          lastName: string
        }>
      )
        .map((teacher) => `${teacher.firstName} ${teacher.lastName}`)
        .join(', ')
    } else {
      // Teachers are just IDs, need to resolve from users store
      const teacherNames = (classTeachers as string[]).map((teacherId) => {
        const teacher = users.find((u: User) => u._id === teacherId)
        return teacher
          ? `${teacher.firstName} ${teacher.lastName}`
          : 'Unknown Teacher'
      })

      return teacherNames.join(', ')
    }
  }

  // Define columns for DataTable
  const columns = useMemo<ColumnDef<Class>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: (info) => (
          <div className="text-sm font-medium text-gray-900">
            {info.getValue() as string}
          </div>
        ),
      },
      {
        accessorKey: 'school',
        header: 'School',
        cell: (info) => {
          const school = info.getValue() as any
          const schoolId = typeof school === 'object' ? school._id : school
          return (
            <div className="text-sm text-gray-900">
              {getSchoolName(schoolId)}
            </div>
          )
        },
      },
      {
        accessorKey: 'students',
        header: 'Students',
        cell: (info) => {
          const students = info.getValue() as any[]
          return (
            <div className="text-sm text-gray-900">
              {students.length} students
            </div>
          )
        },
      },
      {
        accessorKey: 'teachers',
        header: 'Teachers',
        cell: (info) => {
          const teachers = info.getValue() as any[]
          return (
            <div
              className="text-sm text-gray-900"
              title={formatTeachers(teachers)}
            >
              {teachers.length} teacher{teachers.length !== 1 ? 's' : ''}
            </div>
          )
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: (info) => (
          <div className="text-sm text-gray-500">
            {formatDate(info.getValue() as string)}
          </div>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex space-x-2">
            <Button
              onClick={(e) => {
                e.stopPropagation() // Prevent row click from triggering
                navigate(`/user/class/${row.original._id}`)
              }}
              variant="link"
              className="mr-3"
              title="View class details"
            >
              <span>View</span>
            </Button>
          </div>
        ),
      },
    ],
    [schools, users],
  )

  if (teacherClassesLoading || schoolsLoading || usersLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            My Classes
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Classes where you are assigned as a teacher
          </p>
        </div>
      </div>

      {/* Error Display */}
      {teacherClassesError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {teacherClassesError}
        </div>
      )}

      {/* Classes Table */}
      <DataTable
        data={teacherClasses}
        columns={columns}
        searchPlaceholder="Search your classes..."
        emptyStateTitle="No classes assigned"
        emptyStateDescription="You are not currently assigned as a teacher to any classes."
        emptyStateIcon={
          <Icon type="building" className="mx-auto h-12 w-12 text-gray-400" />
        }
        loading={teacherClassesLoading || schoolsLoading || usersLoading}
        showCreateButton={false}
        onRowClick={(classItem) => navigate(`/user/class/${classItem._id}`)}
      />
    </div>
  )
}

export default UserClasses
